import { useEffect, useLayoutEffect, useState, type ReactNode } from "react";
import { catsToAreas } from "../../shared/areas.js";
import { CUSTOM_CADENCES } from "../../shared/ontology.js";
import { LEGAL_DOCS, type LegalDocId } from "../../shared/legal.js";
import { COMPANY_PAGES, LegalBody } from "../screens/company.js";
import { QuestionDetail, saysText } from "../screens/crossfire.js";
import type { ExpertLite } from "../state/api.js";
import { useStore, type CompanyPageId } from "../state/store.js";
import { Avatar } from "./Avatar.js";
import { burdenParts, EvChip, MetaLine, roleLine, YOURS_AVATAR, YoursChip } from "./fragments.js";
import { artImg } from "../assets.js";

/* `.sheet` is `width: min(520px, 100%)` inside 20px of wrap padding, so the
   picture in it is 520px at most and full width on anything narrower. */
const SHEET_SIZES = "(max-width: 560px) 100vw, 520px";

function SheetFrame({ children, intro = false, wide = false, roomy = false, doc = false, legal = false, xf = false }: {
  children: ReactNode;
  intro?: boolean;
  wide?: boolean;
  // The practice modal only: it carries a picture and a header portrait as
  // well as words, so it takes the extra width where the window has it
  // (31 Jul 2026). Prose sheets stay at `wide`, where the measure is right.
  roomy?: boolean;
  // A company document: a rigid title row with the reading underneath it as
  // the only thing that scrolls. Shape, not size, so it composes with a
  // width.
  doc?: boolean;
  // The legal documents: the widest sheet we have (1 Aug 2026, Rob). They
  // run to thousands of words with numbered sections, and a policy squeezed
  // into a 680px column is a policy nobody finishes.
  legal?: boolean;
  // The crossfire question (2 Aug 2026, Rob: "we can make the modal wider as
  // well"). It carries a hero, positions that alternate text and picture, and
  // three passages of article prose, and the alternation needs room to read
  // as alternation rather than as a stack. Its own flag rather than `legal`,
  // because they are the same width today for different reasons and will not
  // stay that way.
  xf?: boolean;
}) {
  const closeSheet = useStore((s) => s.closeSheet);
  return (
    <div className="sheetwrap" onClick={closeSheet}>
      <div
        className={`sheet glass${intro ? " intro" : ""}${wide ? " wide" : ""}${roomy ? " roomy" : ""}${doc ? " doc" : ""}${legal ? " legal" : ""}${xf ? " xf" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

// CC BY and CC BY-SA allow attribution at a linked resource: one credits
// list behind the footer link, never a licence string under every avatar.
function PhotoCreditsSheet() {
  const experts = useStore((s) => s.experts) ?? [];
  const closeSheet = useStore((s) => s.closeSheet);
  const credited = experts.filter((e) => e.photo);
  return (
    <SheetFrame>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <b style={{ fontFamily: "var(--serif)", fontSize: 18 }}>Photo credits</b>
        <button className="btn quiet" style={{ marginLeft: "auto", padding: "5px 9px" }} aria-label="Close" onClick={closeSheet}>
          ✕
        </button>
      </div>
      <p style={{ fontSize: 12.5, color: "var(--t-mid)", lineHeight: 1.6 }}>
        Every portrait here is a photograph of that person, credited to its source below. Anyone without one shows their initials instead.
      </p>
      <div style={{ marginTop: 14 }}>
        {credited.map((e) => {
          const line = [e.photoBy, e.photoLicense].filter(Boolean).join(" · ");
          return (
            <div className="credrow" key={e.id}>
              <Avatar e={e} size="s" />
              <span className="grow">
                <strong>{e.name}</strong>
                <small>
                  {e.photoPage ? (
                    <a href={e.photoPage} target="_blank" rel="noopener noreferrer">
                      {line || "Source"}
                    </a>
                  ) : (
                    line
                  )}
                </small>
              </span>
            </div>
          );
        })}
      </div>
      <p style={{ fontSize: 11.5, color: "var(--t-low)", lineHeight: 1.55, marginTop: 14 }}>
        A face under a real name is an attribution claim like any other, so we do not use stock photographs of strangers to stand in for anyone.
      </p>
    </SheetFrame>
  );
}

// The part both expert sheets share: a face, a name, a sentence, and where
// to read them. Kept in one place so the front door and the follow step
// cannot drift into two designs.
export function ExpertSheetHead({ e, bio, links }: {
  e: ExpertLite;
  bio: string | null;
  links: Array<{ label: string; url: string }>;
}) {
  const closeSheet = useStore((s) => s.closeSheet);
  // The sheet is a reading surface and scrolls: nothing here is ellipsed
  // (28 Jul 2026). The word caps are for rows and cards only.
  const fullBio = (bio ?? "").replace(/<[^>]*>/g, "");
  return (
    <>
      <button className="btn quiet introx" aria-label="Close" onClick={closeSheet}>
        ✕
      </button>
      <div className="introhead">
        <Avatar e={e} size="l" />
        <div style={{ minWidth: 0 }}>
          <b className="introname">{e.name}</b>
          <span className="introrole">{e.role ?? e.lens ?? ""}</span>
        </div>
      </div>
      {fullBio && <p className="introbio">{fullBio}</p>}
      {/* No caution notices here: sheets are introductions. The contested
          record lives on the full profile and the per-person topic screen. */}
      <MetaLine
        style={{ marginTop: 14 }}
        parts={[
          ...(e.cats?.length ? e.cats : e.cat ? [e.cat] : []),
          e.cardCount > 0 && `${e.cardCount} practice${e.cardCount === 1 ? "" : "s"}`,
        ]}
      />
      {links.length > 0 && (
        <div className="introlinks">
          {links.slice(0, 4).map((ch) => (
            <a key={ch.url} href={ch.url} target="_blank" rel="noopener noreferrer">
              {ch.label} ↗
            </a>
          ))}
        </div>
      )}
    </>
  );
}

// Pre-signup: read-only on purpose. There is no account yet, so switches here
// would be settings for a thing that does not exist. The one exception is the
// way in: starting from someone's own page is a vote for that person, so the
// button carries them into the flow and step 3 opens with them already
// followed (30 Jul 2026). It says whose name it is carrying rather than
// following anyone quietly, and step 3 is where it can be undone.
function ExpertIntroSheet({ id }: { id: string }) {
  const experts = useStore((s) => s.experts) ?? [];
  const nav = useStore((s) => s.nav);
  const closeSheet = useStore((s) => s.closeSheet);
  const follows = useStore((s) => s.onboarding.follows);
  const areas = useStore((s) => s.onboarding.areas);
  const toggleObFollow = useStore((s) => s.toggleObFollow);
  const toggleArea = useStore((s) => s.toggleArea);
  const detail = useExpertDetail(id);
  const e = experts.find((x) => x.id === id);
  if (!e) return null;
  const theirAreas = catsToAreas(e.cats?.length ? e.cats : e.cat ? [e.cat] : []);
  return (
    <SheetFrame intro>
      <ExpertSheetHead e={e} bio={detail?.bio ?? e.lens} links={detail?.links ?? []} />
      <button
        className="btn solid lg"
        style={{ marginTop: 18 }}
        onClick={() => {
          // Idempotent: both store actions toggle, and opening this sheet
          // twice must not undo the first visit.
          if (!follows.includes(id)) toggleObFollow(id);
          // Step 1 opens on this person's subjects. They are the reason the
          // reader is here, so making them guess which of sixteen rows the
          // page they just read belongs to is asking a question we already
          // know the answer to. Every tick is still theirs to undo.
          for (const a of theirAreas) if (!areas[a]) toggleArea(a);
          closeSheet();
          nav("onb-areas");
        }}
      >
        Get started with {e.short ?? e.name}
      </button>
    </SheetFrame>
  );
}

// Small on-demand fetch for sheet detail (bio and channel links); cached by
// expert id for the session.
interface SheetDetail {
  bio: string | null;
  links: Array<{ label: string; url: string }>;
  topicIds: string[];
}
const detailCache = new Map<string, SheetDetail>();

export function useExpertDetail(id: string): SheetDetail | null {
  const [detail, setDetail] = useState<SheetDetail | null>(detailCache.get(id) ?? null);
  useEffect(() => {
    if (detailCache.has(id)) {
      setDetail(detailCache.get(id) ?? null);
      return;
    }
    let alive = true;
    void (async () => {
      try {
        const res = await fetch(`/api/experts/${id}`);
        if (!res.ok) return;
        const body = (await res.json()) as {
          expert: { bio: string | null };
          channels: Array<{ label: string; url: string | null }>;
          cards: Array<{ topicId: string | null }>;
        };
        const d: SheetDetail = {
          bio: body.expert.bio,
          links: body.channels
            .filter((c): c is { label: string; url: string } => Boolean(c.url))
            .map((c) => ({ label: c.label, url: c.url })),
          topicIds: [...new Set(body.cards.map((c) => c.topicId).filter((t): t is string => Boolean(t)))],
        };
        detailCache.set(id, d);
        if (alive) setDetail(d);
      } catch {
        // The sheet still renders from the roster row.
      }
    })();
    return () => {
      alive = false;
    };
  }, [id]);
  return detail;
}

// In-app and onboarding sheet: the same head, plus Follow, plus the
// per-person topic switches which appear only once you follow (there is
// nothing to tune about someone sending you nothing). A follow made inside
// a sheet never navigates (spec section 7).
function ExpertPeekSheet({ id }: { id: string }) {
  const experts = useStore((s) => s.experts) ?? [];
  const onboarding = useStore((s) => s.onboarding);
  const toggleObFollow = useStore((s) => s.toggleObFollow);
  const appState = useStore((s) => s.appState);
  const toggleFollow = useStore((s) => s.toggleFollow);
  const setMute = useStore((s) => s.setMute);
  const closeSheet = useStore((s) => s.closeSheet);
  const detail = useExpertDetail(id);
  const e = experts.find((x) => x.id === id);
  if (!e) return null;

  // Signed in, the real follow graph applies; during onboarding the
  // client-held list does.
  const signedIn = Boolean(appState);
  const following = signedIn
    ? (appState?.follows.includes(id) ?? false)
    : onboarding.follows.includes(id);
  // During signup, following from the sheet closes it: the point of the
  // step is choosing several people, so the list should come straight back.
  const follow = () => {
    if (signedIn) {
      toggleFollow(id);
      return;
    }
    toggleObFollow(id);
    if (!following) closeSheet();
  };

  const topicIds = detail?.topicIds ?? [];
  const topics = (appState?.topics ?? []).filter((t) => topicIds.includes(t.id));

  return (
    <SheetFrame intro>
      <ExpertSheetHead e={e} bio={detail?.bio ?? e.lens} links={detail?.links ?? []} />
      {signedIn && following && topics.length > 0 && (
        <div className="field" style={{ marginTop: 16 }}>
          <span className="u">What you get from them</span>
          <p style={{ fontSize: 12, color: "var(--t-low)", lineHeight: 1.5, margin: "6px 0 2px" }}>
            Switch off anything you would rather not get from this person. It stays available from everyone else.
          </p>
          {topics.map((t) => {
            const globalOff = !t.on;
            const muted = appState?.mutes.some((m) => m.expertId === id && m.topicId === t.id) ?? false;
            return (
              <div className="swrow" key={t.id}>
                <span className="grow">
                  <span className="swhead">
                    <strong>{t.label}</strong>
                  </span>
                  {globalOff && <p>You switched this off for everything, back in step 2.</p>}
                </span>
                <button
                  className="sw"
                  role="switch"
                  aria-checked={!muted && !globalOff}
                  aria-label={`${t.label} from ${e.short ?? e.name}`}
                  disabled={globalOff}
                  style={globalOff ? { opacity: 0.35, cursor: "default" } : undefined}
                  onClick={() => void setMute(id, t.id, !muted)}
                />
              </div>
            );
          })}
        </div>
      )}
      <div className="btnrow" style={{ marginTop: 18 }}>
        <button className={`btn ${following ? "quiet" : "solid"}`} onClick={follow}>
          {following ? "Following ✓" : `Follow ${e.short ?? e.name}`}
        </button>
        <button className="btn quiet" onClick={closeSheet}>
          Close
        </button>
      </div>
    </SheetFrame>
  );
}

// One expert's full position on one question: the claim in full, what it
// rests on, the source, and the choice. The lane on the screen is only the
// preview of this.
function LaneSheet({ questionId, laneId }: { questionId: string; laneId: string }) {
  const crossfire = useStore((s) => s.crossfire);
  const appState = useStore((s) => s.appState);
  const experts = useStore((s) => s.experts) ?? [];
  const pickStance = useStore((s) => s.pickStance);
  const closeSheet = useStore((s) => s.closeSheet);
  const nav = useStore((s) => s.nav);
  const q = crossfire?.find((x) => x.id === questionId);
  const lane = q?.lanes.find((l) => l.id === laneId);
  if (!q || !lane) return null;
  const expertId = lane.expertId ?? lane.expertKey;
  const e = experts.find((x) => x.id === expertId);
  const name = e?.name ?? expertId;
  const short = e?.short ?? name;
  const cur = [...q.history].reverse().find((h) => h.pick !== "open") ?? null;
  const isCur = q.status === "decided" && cur?.pick === expertId;
  const urlMatch = /(https?:\/\/[^\s)]+)/.exec(lane.source ?? "");

  return (
    <SheetFrame wide>
      <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 4 }}>
        {e && <Avatar e={e} size="m" />}
        <span style={{ minWidth: 0 }}>
          <b style={{ fontSize: 15, display: "block" }}>{name}</b>
          <span style={{ fontSize: 11.5, color: "var(--t-mid)" }}>{e ? roleLine(e) : ""}</span>
        </span>
        <button className="btn quiet" style={{ marginLeft: "auto", padding: "5px 9px" }} aria-label="Close" onClick={closeSheet}>
          ✕
        </button>
      </div>
      <p className="u" style={{ margin: "10px 0 4px" }}>On: {q.question}</p>
      {/* The position wraps as a full sentence; a chip would run out of room. */}
      <p style={{ fontFamily: "var(--serif)", fontSize: 16, fontWeight: 600, lineHeight: 1.4 }}>{saysText(lane.position)}</p>
      {lane.basis && (
        <div className="field" style={{ marginTop: 16 }}>
          <span className="u">What it rests on</span>
          <p style={{ fontSize: 13, color: "var(--t-mid)", lineHeight: 1.6 }}>{lane.basis}</p>
        </div>
      )}
      {lane.source &&
        (urlMatch ? (
          <a className="source" href={urlMatch[1]} target="_blank" rel="noopener noreferrer">
            <span className="k">Source ↗</span>
            <span className="d">{lane.source}</span>
          </a>
        ) : (
          <div className="source">
            <span className="k">Source</span>
            <span className="d">{lane.source}</span>
          </div>
        ))}
      <div className="btnrow" style={{ marginTop: 18 }}>
        {isCur ? (
          <>
            <span className="btn accent" style={{ cursor: "default" }}>
              Your call since {cur?.date} ✓
            </span>
            <button
              className="btn quiet"
              onClick={() => {
                void pickStance(q.id, "open");
                closeSheet();
              }}
            >
              Undecide
            </button>
          </>
        ) : (
          <>
            {lane.expertId && appState && (
              <button
                className="btn solid"
                onClick={() => {
                  void pickStance(q.id, expertId);
                  closeSheet();
                }}
              >
                Go with {short}
              </button>
            )}
            {lane.expertId && (
              <button
                className="btn quiet"
                onClick={() => {
                  closeSheet();
                  nav("expert", expertId);
                }}
              >
                Their profile ›
              </button>
            )}
          </>
        )}
      </div>
      <p style={{ fontSize: 11.5, color: "var(--t-low)", lineHeight: 1.55, marginTop: 14 }}>
        Practice does not pick a winner here. Whatever you choose is dated, kept, and changeable.
      </p>
    </SheetFrame>
  );
}

// Report a problem with a practice or an expert record: common reasons
// first, freeform detail optional. Filing one is deliberately cheap;
// accuracy is the one editorial ground and this is its front door.
const REPORT_REASONS: Record<"practice" | "expert", string[]> = {
  practice: [
    "Misquotes or misrepresents the source",
    "Attributed to the wrong person",
    "The evidence label seems wrong",
    "Unsafe without more context",
    "The source link is broken",
    "Something else",
  ],
  expert: [
    "A factual error in this profile",
    "An undisclosed commercial relationship",
    "Wrong photo or mistaken identity",
    "This person wants to claim or amend the profile",
    "Something else",
  ],
};

function ReportSheet({ subjectType, subjectId }: { subjectType: "practice" | "expert"; subjectId: string }) {
  const closeSheet = useStore((s) => s.closeSheet);
  const reportIssue = useStore((s) => s.reportIssue);
  // More than one thing can be wrong with the same record (31 Jul 2026,
  // Rob), so the reasons are chosen as a set rather than picked one from a
  // list. They are sent as one line, in the order they are offered.
  const [reasons, setReasons] = useState<string[]>([]);
  const [detail, setDetail] = useState("");
  const [sent, setSent] = useState(false);
  const [failed, setFailed] = useState(false);

  return (
    <SheetFrame>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <b style={{ fontFamily: "var(--serif)", fontSize: 18 }}>Report an issue</b>
        <button className="btn quiet" style={{ marginLeft: "auto", padding: "5px 9px" }} aria-label="Close" onClick={closeSheet}>
          ✕
        </button>
      </div>
      {sent ? (
        <div className="notice safe">
          <span className="ni">✓</span>
          <span>
            <b>Thank you.</b> Accuracy reports are read by a person. If this changes the record, the change is versioned and visible.
          </span>
        </div>
      ) : (
        <>
          <p style={{ fontSize: 12.5, color: "var(--t-mid)", lineHeight: 1.6, marginBottom: 10 }}>
            {subjectType === "practice"
              ? "Something wrong with this practice? Accuracy is the one standard everything here answers to."
              : "Something wrong on this profile? Accuracy is the one standard everything here answers to."}
          </p>
          <div className="field">
            <span className="u">What kind of problem</span>
            <div className="pickrow">
              {REPORT_REASONS[subjectType].map((r) => {
                const on = reasons.includes(r);
                return (
                  <button
                    key={r}
                    className={`btn pick${on ? " on" : ""}`}
                    aria-pressed={on}
                    onClick={() =>
                      setReasons((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]))
                    }
                  >
                    {r}
                    {on && <span aria-hidden="true"> ✓</span>}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="field">
            <span className="u">Anything else we should know, optional</span>
            <textarea
              className="tin"
              rows={3}
              style={{ resize: "vertical" }}
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              aria-label="Additional detail"
            />
          </div>
          {failed && (
            <p className="obfoot" style={{ color: "var(--rose)", marginBottom: 8 }}>
              That did not send. Try again in a moment.
            </p>
          )}
          <button
            className="btn solid lg"
            disabled={reasons.length === 0}
            onClick={() => {
              if (!reasons.length) return;
              // One line, in the order the sheet offers them, so a report
              // reads the same way whatever order they were pressed in.
              const picked = REPORT_REASONS[subjectType].filter((r) => reasons.includes(r)).join("; ");
              void reportIssue(subjectType, subjectId, picked, detail.trim() || null).then((ok) => {
                if (ok) setSent(true);
                else setFailed(true);
              });
            }}
          >
            Send the report
          </button>
        </>
      )}
    </SheetFrame>
  );
}

// The practice record as a modal (28 Jul 2026): whose practice it is,
// where it came from, and the record itself, over whatever surface you
// were on. The dedicated detail screen is gone; every surface opens this.
// Outbound source links are allowed here: this IS the detail surface.
// The disagreement notice never overclaims: "people you follow" only when
// at least two of them are actually in the question; otherwise the experts
// are named, and when the lanes have no resolved names it stays generic.
export function disagreeLead(experts: Array<{ name: string; followed: boolean }>): string {
  if (experts.filter((e) => e.followed).length >= 2) {
    return "People you follow disagree about this.";
  }
  const names = experts.map((e) => e.name);
  if (names.length === 2) return `${names[0]} and ${names[1]} disagree about this.`;
  if (names.length === 3) return `${names[0]}, ${names[1]}, and ${names[2]} disagree about this.`;
  if (names.length > 3) return `${names[0]}, ${names[1]}, and ${names.length - 2} others disagree about this.`;
  return "Experts disagree about this.";
}

function PracticeSheet({ cardId }: { cardId: string }) {
  const s = useStore();
  const loadCardDetail = useStore((st) => st.loadCardDetail);
  const [localVersion, setLocalVersion] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  useEffect(() => {
    void loadCardDetail(cardId);
  }, [cardId, loadCardDetail]);
  const detail = s.cardDetails[cardId];

  if (!detail) {
    return (
      <SheetFrame wide roomy>
        <p className="empty">Loading…</p>
      </SheetFrame>
    );
  }

  const { card, claim, expert } = detail;
  const yours = card.origin === "user";
  const topic = s.appState?.topics.find((t) => t.id === card.topicId);
  const planItem = s.appState?.plan.find((p) => p.cardId === card.id && p.status !== "stopped");
  const chosen = localVersion ?? planItem?.chosenVersion ?? "standard";
  const versions = (["minimal", "standard", "full"] as const).filter((v) =>
    v === "minimal" ? card.versionMinimal : v === "full" ? card.versionFull : card.versionStandard,
  );
  const text =
    chosen === "minimal"
      ? (card.versionMinimal ?? card.do)
      : chosen === "full"
        ? (card.versionFull ?? card.do)
        : (card.versionStandard ?? card.do);
  const short = expert?.short ?? expert?.name ?? "the expert";
  const kind = card.sourceKind ?? "Source";
  const srcIcon = /podcast/i.test(kind) ? "🎧" : /video|tiktok/i.test(kind) ? "▶" : "📄";
  const host = card.sourceUrl ? card.sourceUrl.replace(/^https?:\/\//, "").split("/")[0] : "";
  const menuAt = (ev: React.MouseEvent) => {
    const r = (ev.currentTarget as HTMLElement).getBoundingClientRect();
    return { x: r.right, y: r.bottom };
  };

  return (
    <SheetFrame wide roomy>
      {/* Outside the scrolling part, so the way out never scrolls away. */}
      <button className="btn quiet introx" aria-label="Close" onClick={s.closeSheet}>
        ✕
      </button>
      <div className="sheetscroll">
      {expert && (
        <button
          className="introhead"
          style={{ textAlign: "left" }}
          onClick={() => {
            s.closeSheet();
            s.nav("expert", expert.id);
          }}
        >
          <Avatar e={expert} size="l" />
          <span style={{ minWidth: 0 }}>
            <b className="introname">{expert.name}</b>
            <span className="introrole">{roleLine(expert)}</span>
          </span>
        </button>
      )}
      {yours && (
        <div className="introhead">
          <Avatar e={YOURS_AVATAR} size="l" />
          <span style={{ minWidth: 0 }}>
            <b className="introname">Your practice</b>
            <span className="introrole">Added by you, for you</span>
          </span>
        </div>
      )}

      <MetaLine
        style={{ marginTop: 12 }}
        parts={[
          yours ? <YoursChip /> : card.chip && <EvChip chip={card.chip} />,
          topic?.label,
          // The cadence joins the line here (29 Jul 2026), in the same order
          // the rows on Today read: when, then how long. It used to sit alone
          // under the claim text, which split one thought across two places.
          card.cadence ?? "Daily",
          // The one surface with room for the burden's notes: what it needs of
          // you beyond time and money ("requires equipment", "gym access").
          ...burdenParts(card.burden, true),
        ]}
      />

      <h3 style={{ fontFamily: "var(--serif)", fontSize: 22, fontWeight: 600, lineHeight: 1.3, margin: "10px 0 2px" }}>
        {card.do}
      </h3>
      {card.image && (
        <img className="cardimg" alt="" sizes={SHEET_SIZES} {...artImg(card.image)} />
      )}

      {versions.length > 1 && (
        <div className="seg" style={{ margin: "12px 0 10px" }}>
          {versions.map((v) => (
            <button
              key={v}
              aria-selected={chosen === v}
              onClick={() => {
                setLocalVersion(v);
                if (planItem) void s.setPlanVersion(planItem.id, v);
              }}
            >
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
      )}
      {/* The dose only earns a line when it says something the title did not
          (29 Jul 2026). 247 cards had a ladder built from a template, every
          rung the action with an encouragement prefix; clearing those leaves
          the fallback equal to the heading, and a practice repeated verbatim
          under its own title reads like a rendering fault. */}
      {text !== card.do && (
        <p style={{ fontFamily: "var(--serif)", fontSize: 15, color: "var(--t-hi)", lineHeight: 1.5 }}>{text}</p>
      )}

      {claim?.reasoning && (
        <div className="field" style={{ marginTop: 14 }}>
          <span className="u">Why {short} says so</span>
          <p style={{ fontFamily: "var(--serif)", fontSize: 13.5, color: "var(--t-mid)", lineHeight: 1.6 }}>
            {claim.reasoning}
          </p>
        </div>
      )}
      {yours && card.why && (
        <div className="field" style={{ marginTop: 14 }}>
          <span className="u">Your note</span>
          <p style={{ fontFamily: "var(--serif)", fontSize: 13.5, color: "var(--t-mid)", lineHeight: 1.6 }}>
            {card.why}
          </p>
        </div>
      )}

      {(claim?.population || card.cautions.length > 0) && (
        <div className="field" style={{ marginTop: 12 }}>
          {/* The heading promises only what the record can supply (2 Aug 2026,
              Rob). `population` is inclusive by definition, the ontology's
              section 5 labels naming who a practice is specific to, and nothing
              carries the opposite. So on a card with no cautions the old
              "and who it isn't" advertised a second half that never arrived,
              and a bare label like "Children" read as ambiguously excluded
              rather than addressed. 3,343 of 5,527 practices are in that state. */}
          <span className="u">
            {card.cautions.length > 0 ? "Who this is for, and who it isn’t" : "Who this is for"}
          </span>
          {claim?.population && <p style={{ fontSize: 12.5, color: "var(--t-mid)", lineHeight: 1.6 }}>{claim.population}</p>}
          {card.cautions.length > 0 && (
            <p style={{ fontSize: 12.5, color: "var(--t-mid)", lineHeight: 1.6, marginTop: 6 }}>{card.cautions.join(" ")}</p>
          )}
        </div>
      )}

      {card.disclosure && (
        <div className="notice guard" style={{ marginTop: 12 }}>
          <span className="ni">◆</span>
          <span>
            <b>Commercial relationship, stated:</b> {card.disclosure}.
          </span>
        </div>
      )}

      {detail.questionId && (
        <div className="notice info" style={{ marginTop: 12 }}>
          <span className="ni">⇄</span>
          <span>
            <b>{disagreeLead(detail.questionExperts)}</b>{" "}
            <button
              className="txt"
              style={{ textDecoration: "underline" }}
              onClick={() => {
                s.closeSheet();
                s.nav("crossfire", detail.questionId);
              }}
            >
              See the disagreement ›
            </button>
          </span>
        </div>
      )}

      {yours ? (
        <div className="field" style={{ marginTop: 14 }}>
          <span className="u">Where this came from</span>
          <p style={{ fontSize: 12.5, color: "var(--t-mid)" }}>
            You created this practice. No sources, no evidence label: it is yours.
          </p>
        </div>
      ) : (
        <div className="field" style={{ marginTop: 14 }}>
          <span className="u">Where this came from</span>
          {card.sourceUrl ? (
            <a className="source" href={card.sourceUrl} target="_blank" rel="noopener noreferrer">
              <span className="k">
                {srcIcon} {kind}
              </span>
              <span className="t">{card.sourceTitle ?? card.sourceUrl}</span>
              <span className="d">
                {host}
                {card.sourceDate ? ` · ${card.sourceDate}` : ""} ↗
              </span>
            </a>
          ) : (
            <p style={{ fontSize: 12.5, color: "var(--t-mid)" }}>The source record is on file.</p>
          )}
        </div>
      )}

      </div>
      {/* The answers hold the foot of the sheet while the record scrolls
          above them (31 Jul 2026, Rob): this modal is the one place a
          decision is made, and on a long record the buttons used to be a
          scroll away. They sit outside the scrolling part rather than over
          it, so the footer is just the bottom of the sheet, in the sheet's
          own colour, with nothing passing behind it. */}
      <div className="btnrow sheetacts">
        {yours && planItem ? (
          <>
            <button
              className="btn"
              onClick={(ev) => {
                ev.stopPropagation();
                s.openPopover({ kind: "pause", planItemId: planItem.id, ...menuAt(ev) });
              }}
            >
              Later ▾
            </button>
            <button className="btn" onClick={() => s.openSheet({ kind: "create-practice", cardId: card.id })}>
              Edit
            </button>
            <button
              className="btn quiet"
              style={{ marginLeft: "auto" }}
              onClick={() => {
                if (!confirmDelete) {
                  setConfirmDelete(true);
                  return;
                }
                void s.removePractice(card.id);
              }}
            >
              {confirmDelete ? "Tap again to delete" : "Delete"}
            </button>
          </>
        ) : planItem ? (
          <>
            <button className="btn" disabled>
              In your practice ✓
            </button>
            <button
              className="btn"
              onClick={(ev) => {
                ev.stopPropagation();
                s.openPopover({ kind: "snooze", cardId: card.id, ...menuAt(ev), planItemId: planItem.id });
              }}
            >
              Later ▾
            </button>
            <button
              className="btn quiet"
              onClick={(ev) => {
                ev.stopPropagation();
                s.openPopover({ kind: "decline", cardId: card.id, ...menuAt(ev), planItemId: planItem.id });
              }}
            >
              Stop ▾
            </button>
          </>
        ) : (
          <>
            <button className="btn solid" onClick={() => void s.addCard(card.id, chosen, null)}>
              Start {chosen}
            </button>
            <button
              className="btn"
              onClick={(ev) => {
                ev.stopPropagation();
                s.openPopover({ kind: "snooze", cardId: card.id, ...menuAt(ev) });
              }}
            >
              Later ▾
            </button>
            <button
              className="btn quiet"
              onClick={(ev) => {
                ev.stopPropagation();
                s.openPopover({ kind: "decline", cardId: card.id, ...menuAt(ev) });
              }}
            >
              No thanks ▾
            </button>
          </>
        )}
        {/* Report is a flag glyph everywhere (29 Jul 2026): the words
            wrapped this row onto a second line at narrow widths. */}
        {!yours && (
          <button
            className="btn quiet"
            style={{ marginLeft: "auto" }}
            aria-label="Report issue"
            title="Report issue"
            onClick={() => s.openSheet({ kind: "report", subjectType: "practice", subjectId: card.id })}
          >
            ⚑
          </button>
        )}
      </div>
    </SheetFrame>
  );
}

// Create or edit a practice of your own (29 Jul 2026): what you'll do, a
// structured cadence, an optional note, an optional photo. No evidence
// label, no sources; it is yours and it is private.
function CreatePracticeSheet({ cardId }: { cardId?: string }) {
  const s = useStore();
  const editing = cardId ? s.appState?.cards[cardId] : undefined;
  const detail = cardId ? s.cardDetails[cardId] : undefined;
  const loadCardDetail = useStore((st) => st.loadCardDetail);
  useEffect(() => {
    if (cardId) void loadCardDetail(cardId);
  }, [cardId, loadCardDetail]);

  const [doText, setDoText] = useState(editing?.do ?? "");
  const [cadenceId, setCadenceId] = useState<string>(
    CUSTOM_CADENCES.find((c) => c.cadence === editing?.cadence)?.id ?? "daily",
  );
  const [note, setNote] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [removePhoto, setRemovePhoto] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The note lives only on the detail record; prefill it once it arrives.
  const noteValue = note ?? detail?.card.why ?? "";

  const canSave = doText.trim().length >= 3 && !busy;
  const save = async () => {
    setBusy(true);
    setError(null);
    let id = cardId ?? null;
    if (cardId) {
      await s.updatePractice({
        cardId,
        do: doText.trim(),
        cadenceId,
        note: noteValue.trim() || null,
        removeImage: removePhoto && !file,
      });
    } else {
      id = await s.createPractice({ do: doText.trim(), cadenceId, note: noteValue.trim() || null });
      if (!id) {
        setBusy(false);
        setError("That didn't save. Try again.");
        return;
      }
    }
    if (file && id) {
      const ok = await s.uploadPracticeImage(id, file);
      if (!ok) {
        setBusy(false);
        setError("The practice saved, but the photo didn't upload. You can try it again from Edit.");
        return;
      }
    }
    s.closeSheet();
  };

  return (
    <SheetFrame>
      <button className="btn quiet introx" aria-label="Close" onClick={s.closeSheet}>
        ✕
      </button>
      <h3 style={{ fontFamily: "var(--serif)", fontSize: 20, fontWeight: 600, margin: "2px 0 12px" }}>
        {cardId ? "Edit your practice" : "A practice of your own"}
      </h3>

      <div className="field">
        <span className="u">What will you do?</span>
        <input
          type="text"
          className="tin"
          value={doText}
          maxLength={160}
          placeholder="Spin in circles"
          onChange={(e) => setDoText(e.target.value)}
          aria-label="What will you do"
        />
      </div>

      <div className="field" style={{ marginTop: 12 }}>
        <span className="u">How often?</span>
        <select
          className="tin"
          value={cadenceId}
          onChange={(e) => setCadenceId(e.target.value)}
          aria-label="How often"
        >
          {CUSTOM_CADENCES.map((c) => (
            <option key={c.id} value={c.id}>
              {c.label}
            </option>
          ))}
        </select>
      </div>

      <div className="field" style={{ marginTop: 12 }}>
        <span className="u">A note to yourself, optional</span>
        <textarea
          className="tin"
          rows={3}
          style={{ resize: "vertical" }}
          maxLength={500}
          value={noteValue}
          placeholder="Why this, or how you like to do it"
          onChange={(e) => setNote(e.target.value)}
          aria-label="A note to yourself"
        />
      </div>

      <div className="field" style={{ marginTop: 12 }}>
        <span className="u">A photo, optional</span>
        {editing?.image && !file && !removePhoto && (
          <p style={{ fontSize: 12.5, color: "var(--t-mid)" }}>
            This practice has a photo.{" "}
            <button className="txt" style={{ textDecoration: "underline" }} onClick={() => setRemovePhoto(true)}>
              Remove it
            </button>
          </p>
        )}
        {removePhoto && !file && (
          <p style={{ fontSize: 12.5, color: "var(--t-mid)" }}>The photo comes off when you save.</p>
        )}
        <input
          type="file"
          accept="image/*"
          aria-label="A photo"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        <p style={{ fontSize: 11.5, color: "var(--t-low)", marginTop: 4 }}>
          Up to 8 MB. It stays private to you.
        </p>
      </div>

      {error && (
        <div className="notice guard" style={{ marginTop: 12 }}>
          <span className="ni">◆</span>
          <span>{error}</span>
        </div>
      )}

      <div className="btnrow" style={{ marginTop: 16 }}>
        <button className="btn solid" disabled={!canSave} onClick={() => void save()}>
          {busy ? "Saving…" : cardId ? "Save changes" : "Add to your practice"}
        </button>
        <button className="btn quiet" onClick={s.closeSheet}>
          Cancel
        </button>
      </div>
    </SheetFrame>
  );
}

/* The company documents, as modals (1 Aug 2026, Rob). Terms and privacy came
   first: reading the terms from the signup screen used to navigate away and
   throw away a half-typed address, which is a hard thing to do to someone on
   the last screen of signup. About and Contact followed the same day, for the
   footer's version of the same problem: it is on every screen, so a question
   about who we are should not cost the reader the screen they were on. All of
   them render the same components the routed pages do, so the two copies of
   any document can never drift.

   The heading is a rigid row and the document scrolls beside it, rather than
   the document scrolling under a sticky bar (revised 1 Aug 2026, Rob: the
   text overlapped it). A sticky header cannot hide what passes behind it
   here, because the sheet's own backdrop-filter leaves the header's nothing
   to blur and the glass tone is translucent. Same fix the practice modal's
   footer already uses: a flex column, and only the body scrolls. */
function DocSheet({ title, legal = false, children }: {
  title: string;
  // The policies take the widest sheet we have; About and Contact are a
  // handful of paragraphs and stay at the prose width, where the measure is
  // right.
  legal?: boolean;
  children: ReactNode;
}) {
  const closeSheet = useStore((s) => s.closeSheet);
  return (
    <SheetFrame doc wide={!legal} legal={legal}>
      <div className="dochead">
        <b style={{ fontFamily: "var(--serif)", fontSize: 18 }}>{title}</b>
        <button className="btn quiet" style={{ marginLeft: "auto", padding: "5px 9px" }} aria-label="Close" onClick={closeSheet}>
          ✕
        </button>
      </div>
      <div className="prose docscroll">{children}</div>
    </SheetFrame>
  );
}

function LegalSheet({ doc }: { doc: LegalDocId }) {
  const document = LEGAL_DOCS[doc];
  return (
    <DocSheet title={document.title} legal>
      <LegalBody doc={document} />
    </DocSheet>
  );
}

function CompanySheet({ page }: { page: CompanyPageId }) {
  const { title, Body } = COMPANY_PAGES[page];
  return (
    <DocSheet title={title}>
      <Body />
    </DocSheet>
  );
}

/* Sharing a disagreement out of the app (2 Aug 2026, Rob). The target is the
   public page, /q/<id>, not a deep link into the app: the whole point is that
   the person receiving it has no account, and a link that lands on a sign-in
   wall is not a share.

   Two behaviours from one control. Where the browser has a share sheet, which
   in practice means a phone, it gets the real one, so the link can go to
   Messages or WhatsApp without a round trip through the clipboard. Everywhere
   else it copies, and says so in the button itself rather than in a toast:
   there is no toast anywhere in this product and one control is not a reason
   to build the concept.

   An AbortError is the reader closing the share sheet, which is not a failure
   and must not fall through to a silent copy. Any other error does fall
   through, because a browser that advertises share and then throws still
   leaves the reader wanting the link. */
function ShareQuestion({ id, question }: { id: string; question: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2200);
    return () => clearTimeout(t);
  }, [copied]);

  const url = `${location.origin}/q/${encodeURIComponent(id)}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // Clipboard denied by permission or an insecure origin. Selecting the
      // text is the fallback every browser still has.
      window.prompt("Copy this link", url);
    }
  };
  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: question, url });
        return;
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") return;
      }
    }
    await copy();
  };

  return (
    <button className="btn quiet" onClick={() => void share()} aria-label={`Share: ${question}`}>
      {copied ? "Link copied ✓" : "Share ↗"}
    </button>
  );
}

/* The whole disagreement. This is what the Crossfire screen used to render
   inline for every question at once; the screen is a list now and this is
   where one opens. Everything is the same content in the same order, so a
   reader who knew the old page finds nothing moved. */
function CrossfireSheet({ questionId }: { questionId: string }) {
  const crossfire = useStore((s) => s.crossfire);
  const closeSheet = useStore((s) => s.closeSheet);
  const sheetScroll = useStore((s) => s.sheetScroll);
  const clearSheetScroll = useStore((s) => s.clearSheetScroll);
  /* Put the reader back where they were, before the browser paints, so
     returning from a practice does not flash the top of the question first.
     Layout effect and not effect for exactly that. Consumed once: a later
     re-render must not drag the reader back to a position they have since
     scrolled away from. */
  useLayoutEffect(() => {
    if (sheetScroll === null) return;
    const el = document.querySelector(".sheet");
    if (el) el.scrollTop = sheetScroll;
    clearSheetScroll();
  }, [sheetScroll, clearSheetScroll]);
  const q = crossfire?.find((x) => x.id === questionId);
  if (!q) return null;
  /* The head is the public page's hero (2 Aug 2026, Rob: "should model the
     landing page"): the question set large inside its own illustration, over
     the same bottom-anchored scrim, with the overline and the controls on top
     of it. Every public question now has art of its own, so this is nearly
     always the picture drawn for this question rather than a borrow.

     Without one it falls back to a plain head. The scrim and the white type
     both assume a photograph, and white serif on the sheet's own glass is the
     mistake the public page made on its no-art variant this morning. */
  return (
    <SheetFrame xf>
      {/* The practice modal's shape (2 Aug 2026, Rob: "so we are not
          overlaying text on the image"): the controls, then the question, then
          the picture under it. The question had been set inside the
          illustration over a scrim, which is right on the public page, where
          the hero is the first thing on a page a stranger arrived at cold. In
          a modal it costs twice: the scrim spends most of the picture darkening
          it so type can sit there, and a long question needs so much scrim
          there is little illustration left. Below the words the picture is
          just the picture, and the whole apparatus of white-in-both-themes,
          text shadows and a no-art variant goes with it. */}
      <div className="xfhead">
        <div className="xfhead-top">
          <span className="u">Crossfire</span>
          <div className="xfhead-acts">
            {/* Only where /q/<id> is a real page: two lanes with a named
                expert. A share control that leads to a 404 is worse than
                none. */}
            {q.shareable && <ShareQuestion id={q.id} question={q.question} />}
            <button className="btn quiet xfhead-x" aria-label="Close" onClick={closeSheet}>
              ✕
            </button>
          </div>
        </div>
        <h2 className="xfhead-q">{q.question}</h2>
        {q.image && <img className="xfhead-img" alt="" sizes={SHEET_SIZES} {...artImg(q.image)} />}
      </div>
      <div className="xfsheet-body">
        {/* The notice that nobody you follow is in this one belongs with the
            question, not with the list it is missing from. */}
        <QuestionDetail q={q} outside={!q.mine} />
      </div>
    </SheetFrame>
  );
}

export function SheetHost() {
  const sheet = useStore((s) => s.sheet);
  if (!sheet) return null;
  if (sheet.kind === "legal") return <LegalSheet doc={sheet.doc} />;
  if (sheet.kind === "company") return <CompanySheet page={sheet.page} />;
  if (sheet.kind === "photo-credits") return <PhotoCreditsSheet />;
  if (sheet.kind === "expert-intro") return <ExpertIntroSheet id={sheet.id} />;
  if (sheet.kind === "expert-peek") return <ExpertPeekSheet id={sheet.id} />;
  if (sheet.kind === "practice") return <PracticeSheet cardId={sheet.cardId} />;
  if (sheet.kind === "create-practice") return <CreatePracticeSheet cardId={sheet.cardId} />;
  if (sheet.kind === "crossfire") return <CrossfireSheet questionId={sheet.questionId} />;
  if (sheet.kind === "lane") return <LaneSheet questionId={sheet.questionId} laneId={sheet.laneId} />;
  if (sheet.kind === "report") return <ReportSheet subjectType={sheet.subjectType} subjectId={sheet.subjectId} />;
  return null;
}
